/**
 * Trading Sessions
 *
 * Highlights session ranges for Asia, London, New York, and Kill Zones.
 * Shows the high and low of each session as horizontal lines.
 *
 * Category: Sessions | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

function createSessionIndicator(
  id: string, name: string, shortName: string,
  startHour: number, endHour: number, color: string
): IndicatorDefinition {
  return {
    id,
    name,
    shortName,
    category: "sessions",
    pane: "overlay",
    params: [
      { key: "color", label: "Color", type: "color", default: color },
    ],
    plots: [
      { key: "high", label: `${shortName} High`, color, style: "line", lineWidth: 1 },
      { key: "low", label: `${shortName} Low`, color, style: "line", lineWidth: 1 },
    ],
    calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
      const highData: { time: number; value: number }[] = [];
      const lowData: { time: number; value: number }[] = [];

      let sessionHigh = -Infinity;
      let sessionLow = Infinity;
      let inSession = false;

      for (let i = 0; i < candles.length; i++) {
        const date = new Date(candles[i].time * 1000);
        const hour = date.getUTCHours();
        const nowInSession = startHour < endHour
          ? (hour >= startHour && hour < endHour)
          : (hour >= startHour || hour < endHour);

        if (nowInSession) {
          if (!inSession) {
            sessionHigh = candles[i].high;
            sessionLow = candles[i].low;
            inSession = true;
          } else {
            if (candles[i].high > sessionHigh) sessionHigh = candles[i].high;
            if (candles[i].low < sessionLow) sessionLow = candles[i].low;
          }
          highData.push({ time: candles[i].time, value: sessionHigh });
          lowData.push({ time: candles[i].time, value: sessionLow });
        } else {
          if (inSession) {
            inSession = false;
          }
        }
      }

      return [
        { key: "high", data: highData },
        { key: "low", data: lowData },
      ];
    },
  };
}

export const asiaSessionIndicator = createSessionIndicator(
  "asia_session", "Asia Session", "ASIA", 0, 9, "#FF980080"
);

export const londonSessionIndicator = createSessionIndicator(
  "london_session", "London Session", "LONDON", 7, 16, "#2196F380"
);

export const nySessionIndicator = createSessionIndicator(
  "ny_session", "New York Session", "NY", 12, 21, "#4CAF5080"
);

export const killzoneIndicator = createSessionIndicator(
  "killzone", "Kill Zone", "KILLZONE", 7, 10, "#F4433680"
);
